import { useEffect, useRef } from 'react'

/*
 * Beim Reiterwechsel an den Anfang springen.
 *
 * Wer weit unten in einem langen Reiter steht und dann den Reiter wechselt,
 * bleibt sonst auf derselben Höhe stehen — und landet im neuen Reiter mitten
 * im Inhalt oder, wenn der kürzer ist, unterhalb davon im Leeren. Der Reiter
 * wirkt dann leer, obwohl er gefüllt ist.
 *
 * Welcher Behälter scrollt, ist je nach Ort ein anderer: auf einer Seite das
 * Fenster, in einem Popup dessen eigener Rahmen. Deshalb wird er von der
 * Reiterleiste aus nach oben gesucht, statt einen festen anzunehmen.
 */

const istScrollbar = el => {
	const { overflowY } = window.getComputedStyle(el)
	return /(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight
}

const scrollTarget = node => {
	for (let el = node?.parentElement; el; el = el.parentElement) {
		if (istScrollbar(el)) return el
		/*
		 * Ein Popup liegt fest über der Seite. Sein Inhalt scrollt in einem
		 * eigenen Feld, das neben der Reiterleiste steht statt über ihr — die
		 * Leiste selbst bleibt beim Scrollen ja stehen. Deshalb hier im Popup
		 * danach suchen. Gibt es keins, passt der Inhalt ohne Scrollen: dann
		 * ist nichts zu tun, und die Seite dahinter zu bewegen wäre falsch.
		 */
		if (window.getComputedStyle(el).position === 'fixed') {
			return Array.prototype.find.call(el.querySelectorAll('*'), istScrollbar) || null
		}
	}
	// Sonst scrollt die Seite selbst.
	return document.scrollingElement || document.documentElement
}

/*
 * Ohne Animation: Beim Reiterwechsel tauscht der Inhalt ohnehin komplett aus,
 * da liefe ein sanftes Gleiten der alten Ansicht hinterher. Ein sanfter Lauf
 * bräuchte außerdem Einzelbilder — wo es die nicht gibt (reduzierte Bewegung,
 * eingebettete Ansichten), bliebe die Seite einfach stehen.
 */
const scrollToTop = target => {
	if (target) target.scrollTop = 0
}

/**
 * @param {*} activeTab   aktueller Reiter (jeder Wert, der sich beim Wechsel ändert)
 * @param {object} ref    ref auf die Reiterleiste — von dort wird der scrollbare Behälter gesucht
 * @param {boolean} enabled  auf false setzen, wenn ausnahmsweise ein anderes Ziel
 *                           angesprungen wird (z. B. eine Sprungmarke)
 */
export default function useScrollTopOnTabChange(activeTab, ref, enabled = true) {
	const previous = useRef(activeTab)

	useEffect(() => {
		// Beim ersten Rendern ist noch nichts gewechselt.
		if (previous.current === activeTab) return
		previous.current = activeTab
		if (!enabled) return
		scrollToTop(scrollTarget(ref.current))
	}, [activeTab, enabled, ref])
}
